import { useEffect, useRef, useState, type ComponentType } from "react";
import { useLiquidGlassEnabled } from "../lib/ThemeContext";

type OpticalGlass = typeof import("liquid-glass-react")["default"];
const STILL = Object.freeze({ x: 0, y: 0 });
const REDUCE_GLASS = "(prefers-reduced-transparency: reduce), (prefers-contrast: more), (forced-colors: active)";

/** Decorative optics only. Never pass messages, avatars, or controls through
 * the foreground filter: only a bounded surface's backdrop is refracted.
 * Add `liquid-lens-host` to the containing surface (which owns its rounding).
 */
export function GlassMaterial({ radius = 22, frosted = false }: { radius?: number; frosted?: boolean }) {
  const enabled = useLiquidGlassEnabled();
  const [Engine, setEngine] = useState<ComponentType<Parameters<OpticalGlass>[0]> | null>(null);
  const [size, setSize] = useState("");
  const surface = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    const preference = window.matchMedia(REDUCE_GLASS);
    // WebKit/Firefox don't reliably refract a backdrop with SVG filters. Their
    // CSS material is intentionally retained rather than showing a broken lens.
    const supported = /Chrome\/|Chromium\/|Edg\//.test(navigator.userAgent)
      && CSS.supports("backdrop-filter", "blur(1px)");
    const update = () => {
      if (preference.matches || !supported) { setEngine(null); return; }
      // Client-only, lazy-loaded, local dependency. No remote scripts or images.
      void import("liquid-glass-react").then(module => {
        if (!disposed && !preference.matches) setEngine(() => module.default);
      }).catch(() => { if (!disposed) setEngine(null); });
    };
    update();
    preference.addEventListener("change", update);
    return () => { disposed = true; preference.removeEventListener("change", update); };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !surface.current || typeof ResizeObserver === "undefined") return;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const next = `${Math.round(entry.contentRect.width)}x${Math.round(entry.contentRect.height)}`;
      cancelAnimationFrame(frame);
      // The library measures on mount; remeasure only when the surface resizes,
      // not per mouse movement or on every message render.
      frame = requestAnimationFrame(() => setSize(next));
    });
    observer.observe(surface.current);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [enabled]);

  useEffect(() => {
    const host = surface.current?.parentElement;
    const filter = surface.current?.querySelector("filter");
    if (!host || !filter || !enabled) return;
    // Apply the library's optical map directly to this bounded surface's
    // backdrop, never its foreground. Nested backdrop roots otherwise prevent
    // an empty underlay from sampling the page behind a popup.
    host.style.setProperty("--link-refraction", `url(#${filter.id})`);
    host.style.setProperty("--link-glass-blur", frosted ? "12px" : "3px");
    return () => {
      host.style.removeProperty("--link-refraction");
      host.style.removeProperty("--link-glass-blur");
    };
  }, [Engine, size, enabled, frosted]);

  if (!enabled) return null;
  return <span ref={surface} className="liquid-material" aria-hidden="true" data-liquid-optics={Engine ? "active" : undefined} data-frosted={frosted || undefined}>
    {Engine ? <Engine key={size} className="liquid-optics" cornerRadius={radius}
      displacementScale={48} blurAmount={frosted ? 0.25 : 0} saturation={125} aberrationIntensity={0.8}
      elasticity={0} globalMousePos={STILL} mouseOffset={STILL} mode="standard" padding="0"
      style={{ position: "absolute", top: "50%", left: "50%", width: "100%", height: "100%" }}>
      {null}
    </Engine> : null}
  </span>;
}
